// Per-stage Service Journey controls — upload, approve, send.
//
// Moved out of pages/JourneyDetail.jsx so the Service Desk can show the same
// stepper, with the same working buttons, instead of a read-only copy. Every
// component here took its row id from useParams() before the move, which only
// exists on a routed page (/journey/:row); the Desk selects a group without
// changing the URL. g._row is the same value by construction — JourneyDetail
// itself resolves g via `String(r._row) === String(row)` — so swapping the
// source is a no-op for the page these were written on, and is what makes them
// usable from a second page at all.
import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  useUploadStage,
  useSetDelivery,
  useSendFeedback,
  useSendWorkshopContacts,
  useSendNextServiceDue,
  useSendPreDeliveryFeedback,
  useSendPostDeliveryFeedback,
  useSendWelcomeMessage,
  useSendCustomStage,
  useApprovePreInvoice,
  useFeedback,
} from "../../lib/api";
import { nb, isWaGroupDeleted, fileMatchesAccept, PICKUP_DROP_OPTIONS, DOCUMENT_ACCEPT, FILE_TYPE_ACCEPT } from "../../lib/journey";
import { PickupDropChoice } from "@packs/automotive/web/components/journey/PickupDropChoice";
import { useTenantTerms } from "../../lib/TenantConfigContext";
import { latestFeedbackForGroup, parseRecommendationScore, npsClassify } from "../../lib/feedback";
import { Loader2 } from "lucide-react";
import { Icon } from "../msd/Icon";
import { Tag, LBL, cx } from "../msd/ui";

// One neutral capsule everywhere; a dot carries the state, so a row of
// statuses reads as information rather than a row of warnings.
export const statusChipCls = () => "bg-[rgba(120,120,128,.12)] text-msd-ink border-transparent";

const statusTone = (v) => {
  const s = String(v || "").trim().toLowerCase();
  if (["approve", "approved", "ready", "done", "paid", "joined", "true", "vehicle received", "gatepass created"].includes(s))
    return "green";
  if (["reject", "rejected", "not joined", "false", "reported an issue", "needs improvement"].includes(s)) return "red";
  if (["ask to clarify", "clarify", "callback", "unknown", "pending", "no number"].includes(s)) return "orange";
  return "";
};

const STATUS_DOT = { green: "bg-[#17B26A]", red: "bg-[#F04438]", orange: "bg-[#F79009]" };
export const statusDotCls = (v) => STATUS_DOT[statusTone(v)] || "";

export const StatusChip = ({ title, value }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid={`status-chip-${title.replace(/\s+/g, "-").toLowerCase()}`}>
    <span className={LBL}>{title}</span>
    <Tag small tone={statusTone(value)}>{value}</Tag>
  </div>
);

// The prototype's .tbtn: the solid pill every journey action is.
const TBTN =
  "inline-flex h-8 items-center gap-[6px] self-start whitespace-nowrap rounded-full border-0 px-[14px] text-[13.5px] font-semibold text-white shadow-[0_1px_2px_rgba(16,24,40,.16)] hover:brightness-[.97] disabled:cursor-not-allowed disabled:opacity-60";
const TBTN_BLUE = `${TBTN} bg-msd-btn`;
const TBTN_GREEN = `${TBTN} bg-[#17B26A]`;
// One step's controls, stacked under its row.
const CONTROL = "flex min-w-0 flex-col gap-[10px]";
const NOTES =
  "w-full rounded-xl border-0 bg-msd-ground px-[14px] py-[10px] text-[15px] text-msd-ink outline-none placeholder:text-msd-faint focus:shadow-[0_0_0_2px_#3A5F94]";
const FILE_INPUT =
  "min-w-0 max-w-full text-[13px] text-msd-muted file:mr-2 file:h-8 file:cursor-pointer file:rounded-full file:border-0 file:bg-[rgba(120,120,128,.12)] file:px-[14px] file:text-[13px] file:font-semibold file:text-msd-ink";
const FILE_LINK = "flex items-center gap-[6px] text-[14px] font-medium text-msd-royal hover:underline";
const NOTE = "m-0 text-[12.5px] leading-snug text-msd-muted";
// .seg2.sm
const SEG = "h-7 rounded-[9px] border-0 px-[10px] text-[13px]";
const SEG_ON = "bg-msd-btn font-semibold text-white";
const SEG_OFF = "bg-transparent text-msd-muted";

export function StageUploader({ g, step }) {
  const row = g?._row;
  const queryClient = useQueryClient();
  const upload = useUploadStage();
  const { stageFiles } = useTenantTerms();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [fromCamera, setFromCamera] = useState(false);
  const [notes, setNotes] = useState(g[step.upload.notesCol] || "");
  const [mediaKind, setMediaKind] = useState("image");

  useEffect(() => {
    setNotes(g[step.upload.notesCol] || "");
  }, [g, step.upload.notesCol]);

  const cfg = step.upload;
  const existingLinks = String(g[cfg.linkCol] || "")
    .split(/[\n|]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const hasLinks = existingLinks.length > 0;
  const sent = g[cfg.sentCol] === "Sent";
  const accept = cfg.mediaTypeChoice
    ? mediaKind === "video"
      ? "video/*"
      : "image/*"
    : FILE_TYPE_ACCEPT[stageFiles[cfg.stage]] || cfg.accept || undefined;
  const imagesOnly = String(accept || "").startsWith("image/");
  const videosOnly = String(accept || "").startsWith("video/");
  const videoMultiOk = cfg.stage === "media" || cfg.stage === "work_progress" || cfg.stage === "delivery_media";
  const unit = cfg.multiple && !(cfg.mediaTypeChoice && mediaKind === "video" && !videoMultiOk)
    ? imagesOnly
      ? "images"
      : videosOnly
        ? "videos"
        : "files"
    : videosOnly
      ? "video"
      : imagesOnly
        ? "image"
        : "file";

  const pickMediaKind = (kind) => {
    setMediaKind(kind);
    setFiles([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  // A wrong file is refused at the picker with the reason, not held until the
  // server rejects the upload (the "All files" switch in the dialog defeats
  // the accept filter alone).
  const clearPicked = () => {
    setFiles([]);
    setFromCamera(false);
    if (fileRef.current) fileRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  // On a phone, capture="environment" opens the rear camera; elsewhere it is an
  // ordinary image picker. The photo then goes the same way a chosen file does.
  const takePhoto = (picked) => {
    if (!picked.length) return;
    if (fileRef.current) fileRef.current.value = "";
    setFiles(picked);
    setFromCamera(true);
  };

  const pickFiles = (picked) => {
    setFromCamera(false);
    if (cameraRef.current) cameraRef.current.value = "";
    const bad = picked.filter((f) => !fileMatchesAccept(f, accept));
    if (bad.length) {
      toast.error(`${cfg.title} doesn't accept ${bad.map((f) => f.name).join(", ")}.`);
      if (fileRef.current) fileRef.current.value = "";
      setFiles([]);
      return;
    }
    setFiles(picked);
  };

  const doUpload = async () => {
    if (!files.length) return toast.error("Choose a file first.");
    try {
      await upload.mutateAsync({ row: Number(row), stage: cfg.stage, files, notes: cfg.notesCol ? notes : undefined });
      toast.success(
        files.length > 1 ? `${files.length} ${cfg.title} files uploaded & links saved.` : `${cfg.title} uploaded & link saved.`
      );
      clearPicked();

      await queryClient.invalidateQueries({ queryKey: ["groups"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    }
  };

  return (
    <div className={CONTROL} data-testid={`uploader-${step.key}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={LBL}>{cfg.title} {unit}</span>
        {sent ? (
          <Tag small tone="green">Sent</Tag>
        ) : cfg.notLive ? (
          <Tag small tone="orange">Not live yet</Tag>
        ) : null}
      </div>

      {cfg.status && nb(g[cfg.status.col]) && <StatusChip title={cfg.status.title} value={g[cfg.status.col]} />}

      {hasLinks && (
        <div className="flex flex-col gap-1" data-testid={`file-links-${step.key}`}>
          {existingLinks.map((link, i) => (
            <a
              key={i}
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`file-link-${step.key}-${i}`}
              className={FILE_LINK}
            >
              <Icon name="doc" size={15} stroke={2} /> {existingLinks.length > 1 ? `View file ${i + 1}` : "View uploaded file"}
            </a>
          ))}
        </div>
      )}

      {cfg.notesCol && (
        <textarea
          data-testid={`notes-${step.key}`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional notes…"
          rows={2}
          className={NOTES}
        />
      )}

      {cfg.mediaTypeChoice && (
        <div className="flex flex-wrap items-center justify-between gap-2" data-testid={`media-kind-${step.key}`}>
          <div className={LBL}>Media type</div>
          <div className="inline-flex rounded-xl bg-msd-surface p-[3px] shadow-msd-e1">
            <button
              type="button"
              aria-pressed={mediaKind === "image"}
              onClick={() => pickMediaKind("image")}
              data-testid={`media-kind-image-${step.key}`}
              className={cx(SEG, mediaKind === "image" ? SEG_ON : SEG_OFF)}
            >
              Image
            </button>
            <button
              type="button"
              aria-pressed={mediaKind === "video"}
              onClick={() => pickMediaKind("video")}
              data-testid={`media-kind-video-${step.key}`}
              className={cx(SEG, mediaKind === "video" ? SEG_ON : SEG_OFF)}
            >
              Video
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          data-testid={`file-input-${step.key}`}
          type="file"
          multiple={cfg.mediaTypeChoice && mediaKind === "video" && !videoMultiOk ? undefined : cfg.multiple || undefined}
          accept={accept}
          onChange={(e) => pickFiles(Array.from(e.target.files || []))}
          className={FILE_INPUT}
        />
        {cfg.camera && (!stageFiles[cfg.stage] || stageFiles[cfg.stage] === "image") && (
          <>
            <input
              ref={cameraRef}
              data-testid={`camera-input-${step.key}`}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => takePhoto(Array.from(e.target.files || []))}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              data-testid={`camera-btn-${step.key}`}
              className={TBTN_BLUE}
            >
              Take photo
            </button>
            {fromCamera && files[0] && <span className={NOTE}>{files[0].name} ready</span>}
          </>
        )}
        <button
          onClick={doUpload}
          disabled={upload.isPending}
          data-testid={`upload-btn-${step.key}`}
          className={TBTN_BLUE}
        >
          {upload.isPending && <Loader2 className="animate-spin" size={13} />}
          {hasLinks ? "Replace" : "Upload"}
        </button>
      </div>


      {cfg.notLive && (
        <p className={NOTE}>
          Template pending.
        </p>
      )}
    </div>
  );
}

function PartsApprovalControl({ g }) {
  const row = g?._row;
  const queryClient = useQueryClient();
  const upload = useUploadStage();
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState(g["Parts Approval Notes"] || "");

  const status = g["Parts Approval Status"];
  const sent = g["Parts Approval Sent"] === "Sent" || nb(g["Parts Approval Sent"]);
  const existingLinks = String(g["Parts Approval Link"] || "")
    .split(/[\n|]/)
    .map((s) => s.trim())
    .filter(Boolean);

  const handleSend = async () => {
    if (!notes.trim()) {
      return toast.error("Please enter a note describing the required parts or cost estimate.");
    }
    try {
      await upload.mutateAsync({
        row: Number(row),
        stage: "parts_approval",
        files: files.length ? files : undefined,
        notes,
      });
      toast.success("Parts approval request sent to customer.");
      setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send parts approval request.");
    }
  };

  return (
    <div className={CONTROL} data-testid="parts-approval-control">
      {status && <StatusChip title="Parts Approval" value={status} />}
      {sent && <StatusChip title="Sent Status" value="Sent to customer" />}

      {existingLinks.length > 0 && (
        <div className="flex flex-col gap-1">
          {existingLinks.map((link, i) => (
            <a
              key={i}
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className={FILE_LINK}
            >
              <Icon name="doc" size={15} stroke={2} /> {existingLinks.length > 1 ? `View attachment ${i + 1}` : "View attached image"}
            </a>
          ))}
        </div>
      )}

      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Enter parts details, part numbers, or cost estimates…"
        rows={2}
        data-testid="parts-approval-notes"
        className={NOTES}
      />

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
          data-testid="parts-approval-file-input"
          className={FILE_INPUT}
        />
        <button
          onClick={handleSend}
          disabled={upload.isPending}
          data-testid="send-parts-approval-btn"
          className={TBTN_BLUE}
        >
          {upload.isPending && <Loader2 size={13} className="animate-spin" />}
          {sent ? "Re-send approval request" : "Send approval request"}
        </button>
      </div>

    </div>
  );
}

function DeliveryControl({ g }) {
  const row = g?._row;
  const queryClient = useQueryClient();
  const setDelivery = useSetDelivery();
  const current = g["Delivery Option"];
  // Picked when the car was booked; the delivery step starts from it.
  const booked = g["Pick & Drop"];
  const saved = nb(current) ? current : booked || "";
  // Staff's own pick here, until saved; until then the saved/booked answer shows.
  const [picked, setPicked] = useState(null);
  const choice = picked ?? saved;
  const ready = g["Delivery Status"] === "Ready";
  const delivered = nb(g["Delivery Notified"]);
  const inspection = g["Delivery Inspection Status"];
  const bookedLabel = (PICKUP_DROP_OPTIONS.find(([k]) => k === booked) || [])[1];
  // Staff changed it from the booking's answer at this step.
  const edited = nb(current) && nb(booked) && current !== booked;

  const choose = async (option) => {
    try {
      await setDelivery.mutateAsync({ row: Number(row), option });
      toast.success(`Vehicle delivery marked — ${option}.`);
      setPicked(null);
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed.");
    }
  };

  return (
    <div className={CONTROL} data-testid="delivery-control">
      {delivered && <StatusChip title="Delivery notified" value="Sent to customer" />}
      {nb(inspection) && (
        <StatusChip title="Customer response" value={inspection === "Approve" ? "Vehicle Received" : inspection === "Reject" ? "Reported an issue" : inspection} />
      )}
      {nb(g["Delivery Status"]) && !delivered && (
        <StatusChip
          title="Delivery status"
          value={nb(current) ? `${g["Delivery Status"]} · ${current}${edited ? " (edited)" : ""}` : g["Delivery Status"]}
        />
      )}
      <div className="flex flex-col gap-2">
        <div className={LBL}>Delivery option</div>
        <PickupDropChoice testid="delivery-option" value={choice} onChange={setPicked} />
        {bookedLabel && choice === booked && !nb(current) && (
          <div className="text-[12.5px] text-msd-muted">Chosen at booking. Tap the other option to change it.</div>
        )}
        {bookedLabel && choice && choice !== booked && (
          <div className="text-[12.5px] text-msd-muted" data-testid="delivery-option-edited-note">
            Edited from the booking ({bookedLabel}).
          </div>
        )}
      </div>
      <button
        onClick={() => choose(choice)}
        disabled={!choice || setDelivery.isPending || ((ready || delivered) && choice === current)}
        data-testid="ready-for-delivery-btn"
        className={TBTN_BLUE}
      >
        {setDelivery.isPending && <Loader2 size={13} className="animate-spin" />}
        {ready || delivered ? "Save delivery option" : "Job Card Completed"}
      </button>
    </div>
  );
}

function WelcomeSendControl({ g }) {
  const row = g?._row;
  const queryClient = useQueryClient();
  const sendWelcome = useSendWelcomeMessage();
  const sent = g["Welcome Sent"] === "Sent";

  const send = async () => {
    try {
      await sendWelcome.mutateAsync({ row: Number(row) });
      toast.success(sent ? "Welcome message re-sent." : "Welcome message sent.");
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send welcome message.");
    }
  };

  return (
    <div className={CONTROL} data-testid="welcome-send-control">
      {sent && <StatusChip title="Welcome message" value="Sent" />}
      <button
        onClick={send}
        disabled={sendWelcome.isPending}
        data-testid="send-welcome-inline-btn"
        className={TBTN_BLUE}
      >
        {sendWelcome.isPending && <Loader2 size={13} className="animate-spin" />}
        {sent ? "Re-send welcome message" : "Send welcome message"}
      </button>
    </div>
  );
}

function NextServiceDueSendControl({ g }) {
  const row = g?._row;
  const queryClient = useQueryClient();
  const sendNextServiceDue = useSendNextServiceDue();
  const sent = g["Next Service Due Sent"] === "Sent";

  const send = async () => {
    try {
      await sendNextServiceDue.mutateAsync({ row: Number(row) });
      toast.success(sent ? "Next service reminder re-sent." : "Next service reminder sent.");
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send next service reminder.");
    }
  };

  return (
    <div className={CONTROL} data-testid="next-service-due-send-control">
      {sent && <StatusChip title="Next service reminder" value="Sent" />}
      <button
        onClick={send}
        disabled={sendNextServiceDue.isPending}
        data-testid="send-next-service-due-inline-btn"
        className={TBTN_BLUE}
      >
        {sendNextServiceDue.isPending && <Loader2 size={13} className="animate-spin" />}
        {sent ? "Re-send reminder" : "Send reminder"}
      </button>
    </div>
  );
}

const FILE_ACCEPT = { document: DOCUMENT_ACCEPT, media: "image/*,video/*", album: "image/*,video/*" };

// A stage run from its Service journey settings. The advisor sends the Manual ones from here, and
// adds the file for any stage that takes one when the booking form has none.
function CustomStageControl({ g, step }) {
  const send = useSendCustomStage();
  const setDelivery = useSetDelivery();
  const [files, setFiles] = useState([]);
  const blocks = step.blocks || {};
  const attachment = blocks.attachment && blocks.attachment !== "none" ? blocks.attachment : "";
  const fromBooking = attachment && !!blocks.booking_file;
  const askDelivery = blocks.only_if?.field === "delivery_option";
  const state = step.customState ? step.customState(g) : step.done ? "done" : "";
  if ((blocks.trigger !== "manual" && !attachment) || state) return null;
  const delivery = nb(g["Delivery Option"]) ? g["Delivery Option"] : g["Pick & Drop"] || "";

  const go = async () => {
    try {
      const out = await send.mutateAsync({ row: Number(g._row), stageKey: step.key, files });
      if (out?.reason === "condition_not_met") toast.info(`${step.label} skipped: it is not for this booking.`);
      else toast.success(`${step.label} sent.`);
      setFiles([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${step.label} was not sent. Try again.`);
    }
  };

  const pickDelivery = async (option) => {
    try {
      await setDelivery.mutateAsync({ row: Number(g._row), option });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The delivery option was not saved.");
    }
  };

  return (
    <div className={CONTROL} data-testid={`custom-stage-control-${step.key}`}>
      {askDelivery && (
        <div className="flex flex-col gap-2">
          <div className={LBL}>Delivery option</div>
          <PickupDropChoice testid={`custom-stage-delivery-${step.key}`} value={delivery} onChange={pickDelivery} />
        </div>
      )}
      {attachment && (
        <input
          type="file"
          multiple={attachment === "album"}
          accept={(attachment === "media" && FILE_TYPE_ACCEPT[blocks.file_type]) || FILE_ACCEPT[attachment]}
          onChange={(e) => {
            const picked = Array.from(e.target.files || []);
            if (attachment === "document" && picked.some((f) => !fileMatchesAccept(f, DOCUMENT_ACCEPT))) {
              toast.error("Select a document.");
              return;
            }
            setFiles(picked);
          }}
          className="max-w-full text-[12.5px] text-msd-ink"
          aria-label={`${step.label} file`}
        />
      )}
      <button
        onClick={go}
        disabled={send.isPending || (attachment && !fromBooking && !files.length)}
        className={TBTN_BLUE}
        data-testid={`custom-stage-send-${step.key}`}
      >
        {send.isPending && <Loader2 size={13} className="animate-spin" />}
        {fromBooking && !files.length ? "Send booking file" : "Send"}
      </button>
    </div>
  );
}

function PreDeliveryFeedbackSendControl({ g }) {
  const row = g?._row;
  const queryClient = useQueryClient();
  const sendPreDelivery = useSendPreDeliveryFeedback();
  const { bookServiceUi } = useTenantTerms();
  const sent = g["Pre Delivery Feedback Sent"] === "Sent";
  const status = g["Pre Delivery Feedback Status"];

  const send = async () => {
    try {
      await sendPreDelivery.mutateAsync({ row: Number(row) });
      toast.success(sent ? "Pre delivery feedback re-sent." : "Pre delivery feedback sent.");
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send pre delivery feedback.");
    }
  };

  return (
    <div className={CONTROL} data-testid="pre-delivery-feedback-send-control">
      {sent && <StatusChip title="Pre delivery feedback" value="Sent" />}
      {nb(status) && <StatusChip title="Customer reply" value={status} />}
      <button
        onClick={send}
        disabled={sendPreDelivery.isPending}
        data-testid="send-pre-delivery-feedback-inline-btn"
        className={TBTN_BLUE}
      >
        {sendPreDelivery.isPending && <Loader2 size={13} className="animate-spin" />}
        {sent ? "Re-send template" : "Send template"}
      </button>
    </div>
  );
}

function PostDeliveryFeedbackSendControl({ g }) {
  const row = g?._row;
  const queryClient = useQueryClient();
  const sendPostDelivery = useSendPostDeliveryFeedback();
  const sent = g["Post Delivery Feedback Sent"] === "Sent";

  const send = async () => {
    try {
      await sendPostDelivery.mutateAsync({ row: Number(row) });
      toast.success(sent ? "Post delivery feedback re-sent." : "Post delivery feedback sent.");
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send post delivery feedback.");
    }
  };

  return (
    <div className={CONTROL} data-testid="post-delivery-feedback-send-control">
      {sent && <StatusChip title="Post delivery feedback" value="Sent" />}
      <button
        onClick={send}
        disabled={sendPostDelivery.isPending}
        data-testid="send-post-delivery-feedback-inline-btn"
        className={TBTN_BLUE}
      >
        {sendPostDelivery.isPending && <Loader2 size={13} className="animate-spin" />}
        {sent ? "Re-send template" : "Send template"}
      </button>
    </div>
  );
}

function FeedbackSendControl({ g }) {
  const row = g?._row;
  const queryClient = useQueryClient();
  const sendFeedback = useSendFeedback();
  const { data: fbData } = useFeedback();
  const sent = nb(g["Feedback Notified"]);

  const latestFeedback = latestFeedbackForGroup(fbData?.rows || [], g);
  const recommendScore = latestFeedback ? parseRecommendationScore(latestFeedback["Recommendation Score (1-10)"]) : null;
  const npsGroup = npsClassify(recommendScore);

  const send = async () => {
    try {
      await sendFeedback.mutateAsync({ row: Number(row) });
      toast.success(sent ? "Feedback template re-sent." : "Feedback template sent.");
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed.");
    }
  };

  return (
    <div className={CONTROL} data-testid="feedback-send-control">
      {sent && <StatusChip title="Feedback template" value="Sent" />}
      {npsGroup && (
        <div className="flex flex-wrap items-center gap-2" data-testid="nps-classification">
          <Tag small tone={npsGroup === "Promoter" ? "green" : "red"}>{npsGroup}</Tag>
          <span className="text-[12.5px] text-msd-muted">{recommendScore}/10 recommend score</span>
        </div>
      )}
      <button
        onClick={send}
        disabled={sendFeedback.isPending}
        data-testid="send-feedback-btn"
        className={TBTN_BLUE}
      >
        {sendFeedback.isPending && <Loader2 size={13} className="animate-spin" />}
        {sent ? "Re-send feedback template" : "Send feedback template"}
      </button>
    </div>
  );
}

function WorkshopContactsControl({ g }) {
  const row = g?._row;
  const queryClient = useQueryClient();
  const sendWorkshopContacts = useSendWorkshopContacts();

  const send = async () => {
    try {
      await sendWorkshopContacts.mutateAsync({ row: Number(row) });
      toast.success("Escalation Metrics sent to the WhatsApp group.");
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send Escalation Metrics.");
    }
  };

  return (
    <div className={CONTROL} data-testid="workshop-contacts-control">
      <button
        onClick={send}
        disabled={sendWorkshopContacts.isPending}
        data-testid="send-workshop-contacts-inline-btn"
        className={TBTN_BLUE}
      >
        {sendWorkshopContacts.isPending && <Loader2 size={13} className="animate-spin" />}
        Escalation Metrics
      </button>
    </div>
  );
}

// Guided short flow with "Final bill upload required": the booking's own final
// bill, uploaded here and sent by Approve. No sent column — the upload alone
// never reaches the group.
const FINAL_BILL_STEP = {
  key: "final_bill",
  upload: { stage: "final_bill", linkCol: "Final Bill Link", title: "Final bill", accept: DOCUMENT_ACCEPT },
};

function HyundaiPreInvoiceControl({ g }) {
  const row = g?._row;
  const approve = useApprovePreInvoice();
  const { featureFlags } = useTenantTerms();
  const billRequired = featureFlags?.require_final_bill_upload === true;
  const hasBill = nb(g["Final Bill Link"]);
  const blocked = billRequired && !hasBill;
  const alreadyDone =
    g["Pre Invoice Sent"] === "Sent" ||
    ["approve", "approved"].includes(String(g["Pre Invoice Approval"] || "").trim().toLowerCase());

  const [phase, setPhase] = useState(alreadyDone ? "done" : "idle"); // idle | filling | done
  const [pct, setPct] = useState(alreadyDone ? 100 : 0);

  useEffect(() => {
    if (alreadyDone) {
      setPhase("done");
      setPct(100);
    }
  }, [alreadyDone]);

  const onApprove = async () => {
    if (phase !== "idle" || blocked) return;
    setPct(0);
    setPhase("filling");
    const start = performance.now();
    const duration = 1700;
    await new Promise((resolve) => {
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        setPct(Math.round(t * 100));
        if (t < 1) requestAnimationFrame(tick);
        else resolve();
      };
      requestAnimationFrame(tick);
    });
    setPhase("done");
    try {
      await approve.mutateAsync({ row: Number(row) });
      toast.success("Final bill generated and sent to the WhatsApp group.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to approve pre invoice.");
      setPhase("idle");
      setPct(0);
    }
  };

  return (
    <div
      className={CONTROL}
      data-testid="hyundai-pre-invoice-control"
    >
      {billRequired && phase === "idle" && <StageUploader g={g} step={FINAL_BILL_STEP} />}

      {phase === "idle" && (
        <>
          <button
            type="button"
            onClick={onApprove}
            disabled={blocked}
            data-testid="hyundai-pre-invoice-approve-btn"
            className={TBTN_GREEN}
          >
            <Icon name="check" size={14} stroke={2.6} />
            Approve
          </button>
          {blocked && <p className={NOTE}>Upload the final bill to approve. Approve sends it to the group.</p>}
        </>
      )}

      {(phase === "filling" || phase === "done") && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between text-[12.5px] font-medium text-msd-muted">
            <span>{phase === "done" ? "Final bill generated" : "Generating final bill…"}</span>
            <span className="tabular-nums text-msd-pine-ink">{pct}%</span>
          </div>
          <div className="h-[6px] w-full overflow-hidden rounded-full bg-[rgba(120,120,128,.18)]">
            <div
              className="h-full rounded-full bg-msd-pine transition-[width] duration-75 ease-linear"
              style={{ width: `${pct}%` }}
            />
          </div>
          {phase === "done" && (
            <div className="flex items-center gap-[6px] text-[13px] font-medium text-msd-pine-ink">
              <Icon name="check" size={15} stroke={2.6} />
              Final bill generated
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HyundaiInvoiceAutoControl({ g }) {
  const sent = g["Invoice Sent"] === "Sent";
  return (
    <div
      className={CONTROL}
      data-testid="hyundai-invoice-auto-control"
    >
      <div className="flex items-center justify-between gap-2">
        <span className={LBL}>Final invoice</span>
        {sent ? (
          <Tag small tone="green">Sent</Tag>
        ) : (
          <Tag small tone="orange">Waiting</Tag>
        )}
      </div>
    </div>
  );
}

// The prototype's SICON: one icon and colour per stage.
const STEP_ICON = {
  created: ["people", "#3A5F94"],
  welcome: ["send2", "#3A5F94"],
  invites: ["userplus", "#3A5F94"],
  initial: ["phone", "#F04438"],
  media: ["img", "#3A5F94"],
  estimate: ["doc", "#F79009"],
  jobcard: ["wrench", "#3A5F94"],
  progress: ["wrench", "#3A5F94"],
  parts_approval: ["shield", "#F79009"],
  parts: ["wrench", "#F79009"],
  pre_invoice: ["doc", "#F79009"],
  invoice: ["rupee", "#F79009"],
  pre_delivery_feedback: ["msg", "#17B26A"],
  vehicle_delivered: ["car", "#3A5F94"],
  next_service_due: ["cal", "#3A5F94"],
  post_delivery_feedback: ["star", "#17B26A"],
  feedback: ["star", "#17B26A"],
};

const STATE_TEXT = { completed: "Completed", current: "You are here", next: "Up next", future: "Not started" };

/**
 * One row of the Service Journey: the stage's icon tile, name and state, plus
 * every per-stage control this step needs (upload, approve, send). The single
 * thing both pages — the Journey tab and the Desk's journey panel — render for
 * each stage, so a fix here fixes both at once instead of drifting apart.
 *
 * The Desk passes `card` (the larger row of its current-step card) and
 * `collapsible` (the row folds its controls away; they stay mounted, so a
 * picked file or typed note survives folding). Rows divide themselves, so the
 * `isLast` callers still pass is no longer read.
 *
 * `locked` (the customer has not joined the group yet) switches the controls
 * off like a deleted group does, with a note saying why.
 */
export function StepNode({ step, index, g, card = false, collapsible = false, defaultOpen = true, locked = false }) {
  const { bookServiceUi, featureFlags } = useTenantTerms();
  const engine = featureFlags?.journey_engine === true && !["created", "invites"].includes(step.key);
  const [open, setOpen] = useState(defaultOpen);
  const hyundaiPreInvoice = step.key === "pre_invoice" && bookServiceUi === "hyundai";
  const hyundaiInvoice = step.key === "invoice" && bookServiceUi === "hyundai";
  const hyundaiAutoSent = bookServiceUi === "hyundai" && (step.key === "jobcard" || step.key === "estimate");
  const { state, attention, label, sub } = step;
  const isReject = attention === "reject";
  const isClarify = attention === "clarify";
  const isSuccess = attention === "success";
  // "current" now only ever occurs when step.done is true (see
  // resolveJourney) — kept as an OR for clarity rather than collapsing the
  // two states together.
  const completed = state === "completed" || state === "current";

  const [iconName, iconColor] = STEP_ICON[step.key] || ["circlecheck", "#3A5F94"];
  let tile = { background: "rgba(120,120,128,.16)", color: "var(--faint)" };
  if (isReject) tile = { background: "#F04438" };
  else if (isClarify) tile = { background: "#F79009" };
  else if (completed) tile = { background: iconColor };

  const controls = engine ? [<CustomStageControl key="engine" g={g} step={step} />] : [
    step.upload && !step.capture_from_whatsapp && !hyundaiPreInvoice && !hyundaiInvoice && !hyundaiAutoSent && (
      <StageUploader key="upload" g={g} step={step} />
    ),
    hyundaiAutoSent && (
      <p key="auto" className={NOTE}>
        Sent automatically.
      </p>
    ),
    hyundaiPreInvoice && <HyundaiPreInvoiceControl key="pre-invoice" g={g} />,
    hyundaiInvoice && <HyundaiInvoiceAutoControl key="invoice" g={g} />,
    step.capture_from_whatsapp && (
      <p key="capture" className={NOTE}>
        Captured from WhatsApp.
      </p>
    ),
    (step.partsApproval || step.key === "parts_approval") && <PartsApprovalControl key="parts" g={g} />,
    step.key === "initial" && <WorkshopContactsControl key="contacts" g={g} />,
    step.key === "welcome" && <WelcomeSendControl key="welcome" g={g} />,
    step.key === "pre_delivery_feedback" && <PreDeliveryFeedbackSendControl key="pre-delivery" g={g} />,
    step.key === "next_service_due" && <NextServiceDueSendControl key="next-service" g={g} />,
    step.key === "post_delivery_feedback" && <PostDeliveryFeedbackSendControl key="post-delivery" g={g} />,
    step.delivery && !step.capture_from_whatsapp && <DeliveryControl key="delivery" g={g} />,
    step.feedback && <FeedbackSendControl key="feedback" g={g} />,
    step.custom && !step.capture_from_whatsapp && <CustomStageControl key="custom" g={g} step={step} />,
  ].filter(Boolean);

  const foldable = collapsible && controls.length > 0;
  const shown = !foldable || open;
  const Head = foldable ? "button" : "div";

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04 }}
      className="group/step"
      data-testid={`stepper-node-${step.key}`}
      data-state={state}
    >
      <Head
        {...(foldable ? { type: "button", onClick: () => setOpen((v) => !v), "aria-expanded": open } : {})}
        className={cx(
          "flex w-full items-center gap-3 border-0 bg-transparent text-left text-msd-ink",
          card ? "p-0" : "py-0 pl-3 pr-0",
          foldable && "hover:bg-msd-soft",
        )}
      >
        <span
          className={cx(
            "flex shrink-0 items-center justify-center text-white",
            card ? "h-10 w-10 rounded-[11px]" : "h-[30px] w-[30px] rounded-lg",
          )}
          style={tile}
        >
          <Icon name={isReject || isClarify ? "warn" : iconName} size={card ? 19 : 16} stroke={card ? 2.2 : 2} />
        </span>
        <span
          className={cx(
            "flex min-w-0 grow items-center gap-2",
            !card && "min-h-[50px] border-t border-msd-line py-2 pr-3 group-first/step:border-t-0",
          )}
        >
          <span className="min-w-0 grow">
            <span
              className={cx(
                "block",
                card ? "text-[15.5px] font-semibold" : "text-[15px]",
                isReject ? "text-msd-brick-ink" : isClarify ? "text-msd-amber-ink" : !completed && "text-msd-muted",
                !card && state === "current" && "font-semibold",
              )}
            >
              {label}
            </span>
            <span
              className={cx(
                "block text-msd-muted",
                card ? "text-[13px]" : "mt-px text-[12.5px]",
                state === "current" && "font-medium !text-msd-royal",
              )}
            >
              {STATE_TEXT[state]}
            </span>
          </span>
          {nb(sub) && (
            <Tag small tone={isReject ? "red" : isClarify ? "orange" : isSuccess ? "green" : undefined} className="max-w-[45%]" title={String(sub)}>
              <span className="truncate">{sub}</span>
            </Tag>
          )}
          {completed && !isReject && !isClarify && (
            <span className="flex shrink-0 text-msd-pine">
              <Icon name="circlecheck" size={20} stroke={2} />
            </span>
          )}
          {foldable && (
            <span className="flex shrink-0 text-msd-faint">
              <Icon name={open ? "down" : "right"} size={15} stroke={2.2} />
            </span>
          )}
        </span>
      </Head>
      {/* A deleted WhatsApp group keeps its step details readable, but every
          send, upload and choice inside is switched off (the server refuses
          them too). Same while the customer has not joined. */}
      {controls.length > 0 && (
        <fieldset
          disabled={isWaGroupDeleted(g) || locked}
          className={cx(shown ? "flex" : "hidden", "m-0 min-w-0 flex-col gap-3 border-0 p-0", card ? "pt-3" : "pb-[14px] pl-[54px] pr-3 pt-[2px]")}
        >
          {locked && !isWaGroupDeleted(g) && (
            <p className={NOTE} data-testid={`step-locked-${step.key}`}>
              Customer hasn't joined the group yet. This step unlocks when they join.
            </p>
          )}
          {controls}
        </fieldset>
      )}
    </motion.div>
  );
}
